import { HotelListingRes } from "@/components/main-services/hotels/hotel.type";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  getHotelDetailAction,
  getHotelFilterCriteriaAction,
  GetHotelListingParams,
  getHotelNearbyLandmarks,
  getHotelNearbyProperties,
  getHotelsCarouselAction,
  handleGetHotelListing,
  hotelSearchAction,
  HotelSearchPayload,
} from "@/actions/hotel.action";
import {
  ApiResult,
  HotelCarouselItem,
  HotelCarouselResponse,
  HotelDetailParams,
  HotelFilterCriteriaResponse,
  HotelSearchResponse,
} from "@/types/api";

/** ******************************
 * Hotel query keys
 ****************************** */
const hotelQueryKeys = {
  carousel: () => ["hotel-carousel"] as const,
  detail: (hotelId: string, params: HotelDetailParams) =>
    ["hotel-detail", hotelId, params] as const,
  criteria: () => ["hotel-criteria"] as const,
  nearbyLandmarks: (hotelId: string) =>
    ["hotel-nearby-landmarks", hotelId] as const,
  nearbyProperties: (hotelId: string, params: HotelDetailParams | null) =>
    [
      "hotel-nearby-properties",
      hotelId,
      params?.start ?? "",
      params?.end ?? "",
      params?.adults ?? "",
      params?.children ?? "",
      params?.rooms ?? "",
    ] as const,
  listing: (params: GetHotelListingParams) =>
    ["hotel-listings", params] as const,
};

/** ******************************
 * Hotel Carousel Listing
 ****************************** */
const useHotelsCarousel = (enabled = true) => {
  return useQuery<HotelCarouselResponse, Error, HotelCarouselItem[]>({
    queryKey: hotelQueryKeys.carousel(),
    queryFn: () => getHotelsCarouselAction(),
    enabled,
    staleTime: 1000 * 60 * 5,
    select: (res) => res.data.items,
  });
};

/** ******************************
 * Hotel Detail
 ****************************** */
const useHotelDetail = (hotelId: string, params: HotelDetailParams) => {
  return useQuery({
    queryKey: hotelQueryKeys.detail(hotelId, params),
    queryFn: () => getHotelDetailAction(hotelId, params),
    enabled: Boolean(hotelId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
};

/** ******************************
 * Hotel Filter Criteria
 ****************************** */
const useHotelFilterCriteria = (enabled = true) => {
  return useQuery<HotelFilterCriteriaResponse>({
    queryKey: hotelQueryKeys.criteria(),
    queryFn: () => getHotelFilterCriteriaAction(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

/** ******************************
 * Hotel Nearby Landmarks
 ****************************** */
const useHotelNearbyLandmarks = (hotelId: string, enabled = true) => {
  return useQuery({
    queryKey: hotelQueryKeys.nearbyLandmarks(hotelId),
    queryFn: () => getHotelNearbyLandmarks(hotelId),
    enabled: Boolean(hotelId) && enabled,
    staleTime: 5 * 60 * 1000,
  });
};

/** ******************************
 * Hotel Nearby Properties
 ****************************** */
const useHotelNearbyProperties = (
  hotelId: string,
  params: HotelDetailParams | null,
  enabled = true,
) => {
  return useQuery({
    queryKey: hotelQueryKeys.nearbyProperties(hotelId, params),
    queryFn: () => {
      if (!params) {
        throw new Error("Nearby property params are required");
      }
      return getHotelNearbyProperties(hotelId, params);
    },
    enabled:
      Boolean(hotelId) &&
      Boolean(params?.start) &&
      Boolean(params?.end) &&
      typeof params?.adults === "number" &&
      typeof params?.children === "number" &&
      typeof params?.rooms === "number" &&
      enabled,
    staleTime: 5 * 60 * 1000,
  });
};

/** ******************************
 * Hotel Listing
 ****************************** */
const useGetHotelListing = (
  params: GetHotelListingParams,
  enabled = true,
): UseQueryResult<HotelListingRes[], Error> => {
  return useQuery({
    queryKey: hotelQueryKeys.listing(params),
    queryFn: () => handleGetHotelListing(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    enabled: enabled,
    select: (res: ApiResult<HotelListingRes[]>) => res.data,
  });
};

/** ******************************
 * Handle: Hotel Search
 ****************************** */
const useHandleSearchHotel = () => {
  return useMutation<HotelSearchResponse, Error, HotelSearchPayload>({
    mutationFn: hotelSearchAction,
  });
};

export {
  hotelQueryKeys,
  useHotelsCarousel,
  useHotelDetail,
  useHotelFilterCriteria,
  useHotelNearbyLandmarks,
  useHotelNearbyProperties,
  useGetHotelListing,
  useHandleSearchHotel,
};
